import { createApi } from '@reduxjs/toolkit/query/react'
import { axiosBaseQuery, definedParams } from '../users/api'

import type {
  ConductorApprovalResult,
  ConductorAssignResult,
  ConductorCadenceQueueArgs,
  ConductorCadenceQueueRow,
  ConductorGateVerdict,
  ConductorInstance,
  ConductorInstanceDetail,
  ConductorInstancesArgs,
  ConductorOutboxAction,
  ConductorOutboxArgs,
  ConductorOutboxDetail,
  ConductorPortfolio,
  ConductorRoleAssignee,
  ConductorSlaReport,
  ConductorWorkItem,
  ConductorWorkItemsArgs,
  Paginated,
} from './conductor.types'

export const conductorApi = createApi({
  reducerPath: 'conductor',
  baseQuery: axiosBaseQuery({ baseUrl: '/api/v1/admin' }),
  tagTypes: ['Outbox', 'Portfolio', 'Instance', 'WorkItems', 'RoleAssignees'],
  endpoints: (builder) => ({
    // Portfolio rollup: latest sweep verdicts, instances by playbook/stage
    // and status, open work items by role, outbox/approvals counts.
    getConductorPortfolio: builder.query<ConductorPortfolio, void>({
      query: () => ({ method: 'GET', url: '/conductor/portfolio' }),
      providesTags: ['Portfolio'],
    }),
    getConductorInstances: builder.query<Paginated<ConductorInstance>, ConductorInstancesArgs | void>({
      query: (args) => ({
        method: 'GET',
        url: '/conductor/instances',
        params: definedParams({
          playbookKey: args?.playbookKey,
          status: args?.status,
          stage: args?.stage,
          subjectCuid: args?.subjectCuid,
          page: args?.page,
          resultsPerPage: args?.resultsPerPage,
        }),
      }),
    }),
    getConductorInstance: builder.query<ConductorInstanceDetail, string>({
      query: (cuid) => ({ method: 'GET', url: `/conductor/instances/${cuid}` }),
      providesTags: (_result, _error, cuid) => [{ type: 'Instance', id: cuid }],
    }),
    getConductorSubjectVerdicts: builder.query<ConductorGateVerdict[], string>({
      query: (subjectCuid) => ({
        method: 'GET',
        url: `/conductor/subjects/${encodeURIComponent(subjectCuid)}/verdicts`,
      }),
    }),
    getConductorWorkItems: builder.query<Paginated<ConductorWorkItem>, ConductorWorkItemsArgs | void>({
      query: (args) => ({
        method: 'GET',
        url: '/conductor/work-items',
        params: definedParams({
          ownerRole: args?.ownerRole,
          status: args?.status,
          type: args?.type,
          activeOnly: args?.activeOnly === true ? 'true' : undefined,
          subjectCuid: args?.subjectCuid,
          page: args?.page,
          resultsPerPage: args?.resultsPerPage,
        }),
      }),
      providesTags: ['WorkItems'],
    }),
    getConductorRoleAssignees: builder.query<ConductorRoleAssignee[], string>({
      query: (role) => ({ method: 'GET', url: `/conductor/roles/${encodeURIComponent(role)}/assignees` }),
      providesTags: (_result, _error, role) => [{ type: 'RoleAssignees', id: role }],
    }),
    claimConductorWorkItem: builder.mutation<ConductorAssignResult, string>({
      query: (cuid) => ({ method: 'POST', url: `/conductor/work-items/${cuid}/claim` }),
      invalidatesTags: ['WorkItems', 'Portfolio', 'Instance'],
    }),
    unclaimConductorWorkItem: builder.mutation<ConductorAssignResult, string>({
      query: (cuid) => ({ method: 'POST', url: `/conductor/work-items/${cuid}/unclaim` }),
      invalidatesTags: ['WorkItems', 'Portfolio', 'Instance'],
    }),
    reassignConductorWorkItem: builder.mutation<ConductorAssignResult, { cuid: string, assigneeUserId: string }>({
      query: ({ cuid, assigneeUserId }) => ({
        method: 'POST',
        url: `/conductor/work-items/${cuid}/reassign`,
        data: { assigneeUserId },
      }),
      invalidatesTags: ['WorkItems', 'Portfolio', 'Instance'],
    }),
    getConductorCadenceQueue: builder.query<Paginated<ConductorCadenceQueueRow>, ConductorCadenceQueueArgs | void>({
      query: (args) => ({
        method: 'GET',
        url: '/conductor/cadence-queue',
        params: definedParams({
          gateId: args?.gateId,
          subShape: args?.subShape,
          ownerRole: args?.ownerRole,
          overdue: args?.overdue === true ? 'true' : undefined,
          search: args?.search,
          page: args?.page,
          resultsPerPage: args?.resultsPerPage,
        }),
      }),
    }),
    getConductorSlaReport: builder.query<ConductorSlaReport, number | void>({
      query: (windowDays) => ({
        method: 'GET',
        url: '/conductor/reports/sla',
        params: definedParams({ windowDays: windowDays ?? undefined }),
      }),
    }),
    // KIN-38 approval queue: proposed actions from the conductor outbox.
    getConductorOutbox: builder.query<Paginated<ConductorOutboxAction>, ConductorOutboxArgs | void>({
      query: (args) => ({
        method: 'GET',
        url: '/conductor/outbox',
        params: definedParams({
          status: args?.status,
          autonomyTier: args?.autonomyTier,
          unapprovedOnly: args?.unapprovedOnly === true ? 'true' : undefined,
          actionType: args?.actionType,
          lastErrorContains: args?.lastErrorContains,
          subjectCuid: args?.subjectCuid,
          page: args?.page,
          resultsPerPage: args?.resultsPerPage,
        }),
      }),
      providesTags: ['Outbox'],
    }),
    getConductorOutboxDetail: builder.query<ConductorOutboxDetail, string>({
      query: (cuid) => ({ method: 'GET', url: `/conductor/outbox/${cuid}` }),
      providesTags: (_result, _error, cuid) => [{ type: 'Outbox', id: cuid }, 'Outbox'],
    }),
    // Approve a PENDING APPROVE-tier action; it becomes claimable on the
    // next executor tick. Both mutations act through PrismaOutboxStore
    // server-side, so double-clicks and racing reviewers settle safely.
    approveConductorAction: builder.mutation<ConductorApprovalResult, string>({
      query: (cuid) => ({ method: 'POST', url: `/conductor/outbox/${cuid}/approve` }),
      invalidatesTags: ['Outbox', 'Portfolio', 'Instance'],
    }),
    // Reject (cancel) any PENDING action with a reason — including AUTO-tier
    // rows still inside the delay-send undo window.
    rejectConductorAction: builder.mutation<ConductorApprovalResult, { cuid: string, reason: string }>({
      query: ({ cuid, reason }) => ({
        method: 'POST',
        url: `/conductor/outbox/${cuid}/reject`,
        data: { reason },
      }),
      invalidatesTags: ['Outbox', 'Portfolio', 'Instance'],
    }),
    retryConductorAction: builder.mutation<ConductorApprovalResult, string>({
      query: (cuid) => ({ method: 'POST', url: `/conductor/outbox/${cuid}/retry` }),
      invalidatesTags: ['Outbox', 'Portfolio', 'Instance'],
    }),
    cancelConductorAction: builder.mutation<ConductorApprovalResult, { cuid: string, reason: string }>({
      query: ({ cuid, reason }) => ({
        method: 'POST',
        url: `/conductor/outbox/${cuid}/cancel`,
        data: { reason },
      }),
      invalidatesTags: ['Outbox', 'Portfolio', 'Instance'],
    }),
  }),
})

export const {
  useGetConductorPortfolioQuery,
  useGetConductorInstancesQuery,
  useGetConductorInstanceQuery,
  useGetConductorSubjectVerdictsQuery,
  useGetConductorWorkItemsQuery,
  useGetConductorRoleAssigneesQuery,
  useClaimConductorWorkItemMutation,
  useUnclaimConductorWorkItemMutation,
  useReassignConductorWorkItemMutation,
  useGetConductorCadenceQueueQuery,
  useGetConductorSlaReportQuery,
  useGetConductorOutboxQuery,
  useGetConductorOutboxDetailQuery,
  useApproveConductorActionMutation,
  useRejectConductorActionMutation,
  useRetryConductorActionMutation,
  useCancelConductorActionMutation,
} = conductorApi

export const conductorReducer = conductorApi.reducer
